// The posting step gate and the renderer use the same current option list.
export function resolveOptions(field, values, ctx) {
  const opts = typeof field.options === "function" ? field.options(values, ctx) : field.options
  if (!Array.isArray(opts)) return []
  return opts.map((option) => {
    if (option && Array.isArray(option.options)) {
      return { ...option, options: resolveOptions({ options: option.options }, values, ctx) }
    }
    return ["string", "number", "boolean"].includes(typeof option)
      ? { value: option, label: String(option) } : option
  })
}

// StepValidations.numerical_validation uses this ceiling unless a field says otherwise.
export const SERVER_WORD_MAX = 1000000

function wordCountIssue(field, raw, required) {
  if (!field.key || field.fixedLabel) return null
  if (isEmpty(raw)) return required ? "This field is required" : null
  if (typeof raw !== "number" && (typeof raw !== "string" ||
    !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(raw.trim()))) {
    return "Enter a valid word count"
  }
  const value = Number(raw)
  if (!Number.isFinite(value)) return "Enter a valid word count"
  if (field.min != null && value < field.min) return `At least ${field.min.toLocaleString()} words`
  const max = field.max ?? SERVER_WORD_MAX
  if (value > max) return `No more than ${max.toLocaleString()} words`
  if (field.step != null && (value - (field.min ?? 0)) % field.step !== 0) {
    return `Use steps of ${field.step.toLocaleString()} words`
  }
  return null
}

function choiceIssue(field, values, ctx, required) {
  const raw = values[field.key]
  if (isEmpty(raw)) return required ? "empty" : null
  const options = resolveOptions(field, values, ctx)
  const choices = field.type === "select"
    ? options.flatMap((option) => Array.isArray(option?.options) ? option.options : [option])
    : options
  if (!choices.some((choice) => choice?.value != null)) return null
  return choices.some((choice) => choice?.value != null &&
    String(choice.value) === String(raw)) ? null : "stale"
}

// Ruby String#split separates on ASCII whitespace when counting draft words.
export function countWords(str) {
  if (!str) return 0
  return (String(str).match(/[^ \t\n\v\f\r]+/g) || []).length
}

export function visibleFieldsOf(step, values, ctx) {
  return (step?.fields || []).filter((field) => !field.when || field.when(values, ctx))
}

// ── client validation (gates Continue; the server StepValidations re-checks) ──

export function isEmpty(value) {
  if (value == null) return true
  if (typeof value === "string") return value.trim() === ""
  if (Array.isArray(value)) return value.length === 0
  return false
}

// Fill a service's config defaults into its posting sub-state, but only where the
// existing value is empty/absent (isEmpty). A real drafted or customer-entered
// value always wins; a null/empty column carried in from hydrateFromDraft does
// NOT clobber the design default. General across any service that declares
// `config.defaults` — not Book Writing specific.
export function mergeDefaults(defaults, existing = {}) {
  const merged = { ...existing }
  for (const [k, v] of Object.entries(defaults)) {
    if (isEmpty(merged[k])) merged[k] = v
  }
  return merged
}

export function fieldOk(field, values, ctx) {
  const required = typeof field.required === "function" ? !!field.required(values, ctx) : !!field.required

  switch (field.type) {
    case "consent":
      return required ? !isEmpty(values[field.key || "ai_consent"]) : true

    case "triggerWarning": {
      if (!values.trigger_warning) return true
      const hasCats = Array.isArray(values.trigger_warning_warnings) && values.trigger_warning_warnings.length > 0
      const hasOther = !isEmpty(values.trigger_warning_other)
      return hasCats || hasOther
    }

    case "checkgroup":
    case "gallery":
    case "palette": {
      const arr = Array.isArray(values[field.key]) ? values[field.key] : []
      if (required && arr.length === 0) return false
      if (field.min && arr.length < field.min) return false
      if (field.max && arr.length > field.max) return false
      return true
    }

    case "wordcount": {
      // Fixed word-count fields are display-only. Editable counts use the
      // same minimum, maximum and increment for Continue and inline copy.
      return !wordCountIssue(field, values[field.key], required)
    }

    case "radio":
    case "select":
      return !choiceIssue(field, values, ctx, required)

    case "textarea":
    case "note": {
      // A declared word range is the server's range (StepValidations
      // .valid_word_count), so enforce it at the step that collects it rather
      // than letting the customer reach final review and take a 422.
      const raw = values[field.key]
      if (isEmpty(raw)) return !required

      const words = countWords(raw)
      if (field.minWords && words < field.minWords) return false
      if (field.maxWords && words > field.maxWords) return false
      return true
    }

    case "sliders": {
      if (!field.minMoved) return true
      const moved = (field.axes || []).filter((a) => {
        const v = values[a.key]
        return typeof v === "number" && v !== 3
      }).length
      return moved >= field.minMoved
    }

    case "toggle": {
      if (values[field.key] && field.detailsRequired && field.detailsKey) {
        return !isEmpty(values[field.detailsKey])
      }
      return true
    }

    case "file":
      return required ? !!(values[field.key] && values[field.key].name) : true

    default:
      return required ? !isEmpty(values[field.key]) : true
  }
}

export function stepIsValid(step, values, ctx) {
  return visibleFieldsOf(step, values, ctx).every((field) => fieldOk(field, values, ctx))
}

// Per-field validation message for the design's inline-error idiom (WI-A20).
// Reuses fieldOk to decide unmet, then returns the design's message per type
// (service.html `fieldError`, L276-282). null = the field is satisfied (or
// hidden), so no error renders.
export function fieldError(field, values, ctx) {
  if (field.when && !field.when(values, ctx)) return null
  if (fieldOk(field, values, ctx)) return null

  switch (field.type) {
    case "radio":
    case "consent":
      return field.type === "radio" && choiceIssue(field, values, ctx, true) === "stale"
        ? "Choose a current option" : "Please choose an option"

    case "select":
      return choiceIssue(field, values, ctx, true) === "stale"
        ? "Choose a current option" : "Please select an option"

    case "wordcount":
      return wordCountIssue(field, values[field.key], true)

    case "textarea":
    case "note": {
      if (isEmpty(values[field.key])) return "This field is required"

      const words = countWords(values[field.key])
      if (field.minWords && words < field.minWords) return `At least ${field.minWords} words`
      if (field.maxWords && words > field.maxWords) return `No more than ${field.maxWords} words`
      return "This field is required"
    }

    case "gallery":
    case "palette":
    case "checkgroup":
      return `Pick at least ${field.min ?? 1}`

    case "count":
      return "Please enter an amount"

    case "file":
      return "A file is required"

    case "toggle":
      return "Please complete the details"

    case "triggerWarning":
      return "Select at least one category"

    default:
      return "This field is required"
  }
}
